import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"
import { useResolveCls } from "../contexts/breakpoint-context"
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion"

export function AccordionRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const rcls = useResolveCls()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("accordion")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const handleValueChange = (value: string | string[]) => {
    dispatchEvent(element.id, "toggle", { value })
  }

  return (
    <Accordion
      multiple
      className={cn("w-full", element.className)}
      data-testid={`accordion-${element.id}`}
      data-atom-type="accordion"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      onValueChange={handleValueChange}
    >
      {element.children?.map((section, index) => {
        // Support both flat items (legacy) and nested accordion-item structure (v2)
        const isAccordionItem = section.type === "accordion-item"

        // v2: accordion-item may contain accordion-trigger + accordion-content children
        const triggerChild = isAccordionItem
          ? section.children?.find(c => c.type === "accordion-trigger")
          : undefined
        const contentChild = isAccordionItem
          ? section.children?.find(c => c.type === "accordion-content")
          : undefined

        const title = triggerChild?.text
          ? String(resolveSymbol(triggerChild.text) ?? "")
          : section.text
          ? String(resolveSymbol(section.text) ?? "")
          : `Section ${index + 1}`

        const contentChildren = contentChild?.children ?? section.children ?? []

        return (
          <AccordionItem key={section.id} value={section.id} className={cn(rcls(section.className))}>
            <AccordionTrigger className={cn(rcls(triggerChild?.className))}>
              {title}
            </AccordionTrigger>
            <AccordionContent className={cn(rcls(contentChild?.className))}>
              {contentChildren.map((child) => (
                <ElementRenderer key={child.id} element={child} />
              ))}
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}
